import { Injectable, inject } from '@angular/core';
import {
  Action,
  Selector,
  State,
  type StateContext,
  StateToken,
} from '@ngxs/store';
import { tap } from 'rxjs/operators';

import { ShareService } from '@plugins/client/share/share.service';
import type { Facet } from '@plugins/shared/types/post/facet';
import { FilterOptionData } from '@plugins/shared/types/post/filter';

import { InstanceStateModel } from '../../../types';
import { InstanceService } from '../../services/instance.service';

import {
  CountByStatus,
  LoadInstance,
  UpdateInstance,
} from './instance.actions';

const INSTANCE_STATE_TOKEN = new StateToken<InstanceStateModel>('instance');

@State<InstanceStateModel>({
  name: INSTANCE_STATE_TOKEN,
  defaults: {
    instance: null,
  },
})
@Injectable()
export class InstanceState {
  private readonly instanceService = inject(InstanceService);
  private readonly shareService = inject(ShareService);

  @Selector()
  static instance(state: InstanceStateModel) {
    return state.instance;
  }

  @Selector()
  static settings(state: InstanceStateModel) {
    return state.instance.settings;
  }

  @Selector()
  static instanceId(state: InstanceStateModel) {
    return state.instance.id;
  }

  @Selector()
  static primaryColor({ instance }: InstanceStateModel): string {
    return instance.settings.primaryColor;
  }

  @Selector()
  static facets(state: InstanceStateModel) {
    return state.instance.facets;
  }

  @Selector()
  static facetEntities(state: InstanceStateModel) {
    return state.instance.facets.reduce(
      (acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      },
      {} as Record<string, Facet>,
    );
  }

  @Selector()
  static facetFilterOptions(state: InstanceStateModel): FilterOptionData[] {
    return state.instance.facets.reduce<FilterOptionData[]>(
      (options, facet) => {
        if (!facet.filterable) {
          return options;
        }
        for (const option of facet.facetOptions) {
          options.push({
            facetLabel: facet.label,
            optionLabel: option.label,
            value: `facet=${option.id}`,
          });
        }
        return options;
      },
      [],
    );
  }

  @Selector()
  static selectionFacets(state: InstanceStateModel) {
    return state.instance.facets.filter((facet) => facet.type === 'select');
  }

  @Action(LoadInstance)
  loadInstance({ patchState }: StateContext<InstanceStateModel>) {
    return this.instanceService.load().pipe(
      tap((instance) => {
        patchState({ instance });
        this.shareService.setExternalEnabled(
          instance.settings.externalShareEnabled,
        );
      }),
    );
  }

  @Action(UpdateInstance)
  updateInstance(
    { patchState }: StateContext<InstanceStateModel>,
    { instanceChanges }: UpdateInstance,
  ) {
    return this.instanceService.update(instanceChanges).pipe(
      tap((instance) => {
        patchState({ instance });
        this.shareService.setExternalEnabled(
          instance.settings.externalShareEnabled,
        );
      }),
    );
  }

  @Action(CountByStatus)
  countByStatus({ getState, patchState }: StateContext<InstanceStateModel>) {
    return this.instanceService.countByStatus().pipe(
      tap((countByStatus) => {
        const { instance } = getState();
        patchState({
          instance: { ...instance, ...countByStatus },
        });
      }),
    );
  }
}
